import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Router, Switch, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { Provider } from 'react-redux';
import { CssBaseline, StylesProvider, ThemeProvider } from '@material-ui/core';
import { LoadingSpinner, EnvironmentSwitcher } from '@bighealth/react-limbix-ui';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';

import App from './App';
import history from './utils/history';
import './i18n';
import { client } from './apollo';
import { oldTheme } from './theme';
import store from './store';
import './index.css';

import AppStoreRedirector from './pages/Onboarding/components/AppStoreRedirector/AppStoreRedirector';
import PostHogPageviewTracker from './PostHogPageViewTracker';
import { projectApiKey, postHogHost } from './utils/posthog';
import { UserUidProvider } from './hooks/useUserUid';

import Resources from './pages/Resources';

import UtmCapture from './components/UtmCapture/UtmCapture';
import OrgSlugCapture from './components/OrgSlugCapture/OrgSlugCapture';
import SingularInit from './components/SingularInit';

import CheckYourOrganization from './pages/PreOnboarding/steps/CheckYourOrganization/CheckYourOrganization';
import AccessCodeSpark from './pages/PreOnboarding/steps/AccessCodeSpark';
import AccessCodeEntry from './pages/PreOnboarding/steps/AccessCodeSpark/AccessCodeEntry';
import CreateAccount from './pages/PreOnboarding/steps/AccessCodeSpark/CreateAccount';
import SmsVerify from './pages/PreOnboarding/steps/AccessCodeSpark/SmsVerify';
import UserInfoConfirmation from './pages/PreOnboarding/steps/AccessCodeSpark/UserInfoConfirmation';

import {
  Login,
  ResetPassword,
  ResetPasswordForm,
  ResetPasswordFormRedirect,
  KaiserSignupPage,
  Onboarding,
  SleepioWellnessOnboarding,
  PreOnboarding,
  SoleraAccountCreation,
  SingularLanding,
} from '@/pages';

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: 'https://2ef2b1df37e1499a8f1d971534713649@o58970.ingest.sentry.io/5275036',
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: process.env.REACT_APP_DEPLOY_ENV,
    release: `parents@${process.env.APP_VERSION}`,
  });
}

if (projectApiKey && typeof window !== 'undefined') {
  posthog.init(projectApiKey, {
    api_host: postHogHost,
    // don't double capture pageviews, this is handled by PostHogPageviewTracker
    capture_pageview: false,
  });
}

ReactDOM.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <StylesProvider injectFirst>
        <ThemeProvider theme={oldTheme}>
          <CssBaseline />
          <Provider store={store}>
            <Suspense fallback={<LoadingSpinner />}>
              <ApolloProvider client={client}>
                <UserUidProvider>
                  <Router history={history}>
                    <UtmCapture />
                    <OrgSlugCapture />
                    <PostHogPageviewTracker />
                    <SingularInit />
                    <Switch>
                      <Route component={Login} path="/login" />
                      <Route component={Resources} path="/:product/resources" />
                      <Route component={ResetPassword} path="/reset-password" />
                      <Route component={ResetPasswordForm} path="/reset-password-form/:userUid/:token" />
                      <Route component={ResetPasswordFormRedirect} path="/reset-password-form-redirect" />
                      <Route component={KaiserSignupPage} path="/organization-signup/kaiser" />
                      <Route
                        component={SleepioWellnessOnboarding}
                        exact
                        path="/coverage/:product/:slug/:careTeamUid/:token"
                      />
                      <Route
                        component={SoleraAccountCreation}
                        exact
                        path="/solera/:product"
                      />
                      <Route
                        component={CheckYourOrganization}
                        path="/activate/:product/employers"
                      />
                      <Route
                        component={CheckYourOrganization}
                        path="/activate/:product/providers"
                      />
                      <Route
                        component={AccessCodeSpark}
                        exact
                        path="/activate/:product/access-code"
                      />
                      <Route
                        component={AccessCodeEntry}
                        exact
                        path="/activate/:product/access-code/entry"
                      />
                      <Route
                        component={CreateAccount}
                        exact
                        path="/activate/:product/access-code/entry/create-account"
                      />
                      <Route
                        component={SmsVerify}
                        exact
                        path="/activate/:product/sms/verify"
                      />
                      <Route
                        component={UserInfoConfirmation}
                        exact
                        path="/activate/:product/user-info/confirmation"
                      />
                      <Route component={Onboarding} path="/coverage/:product/:slug" />
                      <Route component={PreOnboarding} path="/activate/:product/:slug" />
                      <Route component={AppStoreRedirector} path="/appstore/:product" />
                      <Route component={SingularLanding} exact path="/s" />
                      <Route component={App} />
                    </Switch>
                    <EnvironmentSwitcher />
                  </Router>
                </UserUidProvider>
              </ApolloProvider>
            </Suspense>
          </Provider>
        </ThemeProvider>
      </StylesProvider>
    </PostHogProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);
